import { useEffect } from "react";
import { useFetcher, useMatches, useRouteLoaderData } from "react-router";
import { $path } from "safe-routes";
import type { SerializeFrom } from "~/modules/routing/types";
import type { loader as rootLoader } from "~/root";
import type { loader as appLoader } from "~/routes/_app";
import type { loader as accountLoader } from "~/routes/account";

function isAuth(
  auth: unknown,
): auth is SerializeFrom<typeof rootLoader>["auth"] {
  return (
    !!auth &&
    typeof auth === "object" &&
    "user" in auth &&
    !!auth.user &&
    typeof auth.user === "object" &&
    "uid" in auth.user &&
    typeof auth.user.uid === "string"
  );
}

/**
 * Returns optional current context from root.
 * This context can be used anywhere but should be considered as stale.
 * If you need an up to date context, use `useAppContext` if you're in a route/_app/* route.
 */
export function useOptionalRootContext() {
  const matches = useMatches();
  const optionalAppContext = useOptionalAppContext();
  const data = useRouteLoaderData<typeof rootLoader>("root");

  if (matches.some((match) => match.id === "routes/_app")) {
    return optionalAppContext;
  }

  if (!data || !("context" in data)) return undefined;

  return data.context;
}

/**
 * Returns current context from root.
 * This context can be used anywhere but should be considered as stale.
 * If you need an up to date context, use `useAppContext` if you're in a route/_app/* route.
 */
export function useRootContext() {
  const rootContext = useOptionalRootContext();

  if (!rootContext) {
    throw new Error(
      "No context found in root loader, but auth is required by `useRootContext`. If use is optional try `useOptionalRootContext` instead.",
    );
  }

  return rootContext;
}

/**
 * Returns the optional up to date context from routes/_app.
 * You can safely use this inside of routes/_app/* routes to get the accurate context.
 */
export function useOptionalAppContext() {
  const data = useRouteLoaderData<typeof appLoader>("routes/_app");

  if (!data || !("context" in data)) return undefined;

  return data.context;
}

/**
 * Returns the up to date context from routes/_app.
 * You can safely use this inside of routes/_app/* routes to get the accurate context.
 */
export function useAppContext() {
  const appContext = useOptionalAppContext();

  if (!appContext) {
    throw new Error(
      "No context found in app loader, but auth is required by `useAppContext`. If use is optional try `useOptionalAppContext` instead.",
    );
  }

  return appContext;
}

export function useOptionalAuth() {
  const data = useRouteLoaderData<typeof rootLoader>("root");

  if (!data || !("auth" in data) || !isAuth(data.auth)) return undefined;

  return data.auth;
}

export function useAuth() {
  const auth = useOptionalAuth();

  if (!auth) {
    throw new Error(
      "No auth found in root loader, but auth is required by `useAuth`. If use is optional try `useOptionalAuth` instead.",
    );
  }

  return auth;
}

export function useAccountCounts() {
  const data = useRouteLoaderData<typeof accountLoader>("routes/account");
  const fetcher = useFetcher<typeof accountLoader>({ key: "account-counts" });

  useEffect(() => {
    if (!data) {
      fetcher.load($path("/account"));
    }
  }, []);

  return data ?? fetcher.data;
}
